import React from 'react';
import styled from 'styled-components';
import { Body1, Body5, Container, Modal } from '@ui';
import { SkeletonButton, TransparentFlatButton } from '@ui/buttons';
import { Working } from '@ui/Working';

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
const RowList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 60vh;
  overflow-y: auto;
`;
const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled, $isActive }) => ($disabled && !$isActive ? 0.5 : 1)};
  transition: background 0.15s;

  &:hover {
    background: ${({ $disabled, theme }) => ($disabled ? 'transparent' : theme.themeColors.lowContrastBorder)};
  }
`;
const RowTopLine = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
const RequestText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
`;
const RowError = styled.div`
  color: ${({ theme }) => theme.themeColors.error};
`;
const EmptyState = styled.div`
  padding: 24px 8px;
  text-align: center;
`;
const ListErrorState = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px 8px;
  text-align: center;
`;
const DismissWrapper = styled.div`
  text-align: center;
`;

const formatRowDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const SessionPickerModal = ({
  onDismiss,
  sessions,
  isLoadingList,
  listError,
  onRefetch,
  pendingDetailId,
  detailErrorById,
  onPick,
}) => {
  const [modalProps] = React.useState({});

  const isAnyPending = pendingDetailId != null;

  const handleRowClick = (id) => () => {
    if (isAnyPending) return;
    onPick(id);
  };

  return (
    <Modal onDismiss={onDismiss} modalProps={modalProps}>
      <Container bottom={16}>
        <HeaderRow>
          <Body1>Resume Previous Session</Body1>
        </HeaderRow>
      </Container>

      {isLoadingList ? (
        <Working message="Loading previous sessions" />
      ) : listError ? (
        <ListErrorState>
          <Body5>{listError}</Body5>
          <Container>
            <SkeletonButton onClick={onRefetch}>Retry</SkeletonButton>
          </Container>
        </ListErrorState>
      ) : sessions.length === 0 ? (
        <EmptyState>
          <Body5 isLowContrast>No previous sessions in the last 30 days.</Body5>
        </EmptyState>
      ) : (
        <RowList>
          {sessions.map((s) => {
            const isActive = pendingDetailId === s.id;
            const rowError = detailErrorById[s.id];
            return (
              <Row key={s.id} $disabled={isAnyPending} $isActive={isActive} onClick={handleRowClick(s.id)}>
                <RowTopLine>
                  <RequestText>
                    <Body5>{s.request}</Body5>
                  </RequestText>
                  <Body5 isLowContrast>{formatRowDate(s.created_at)}</Body5>
                </RowTopLine>
                {isActive ? <Body5 isLowContrast>Loading…</Body5> : null}
                {rowError ? (
                  <RowError>
                    <Body5>{rowError}</Body5>
                  </RowError>
                ) : null}
              </Row>
            );
          })}
        </RowList>
      )}

      <Container top={24}>
        <DismissWrapper>
          <TransparentFlatButton onClick={onDismiss} disabled={isAnyPending}>
            Dismiss
          </TransparentFlatButton>
        </DismissWrapper>
      </Container>
    </Modal>
  );
};
